import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { postcodeApi, ApiError, type PostcodeSuggestion } from "@/lib/api";
import { MapPinned } from "lucide-react";

/**
 * "I know my postcode" input — partial postcode -> NIPOST suggestions ->
 * pick one -> fills the paired address field. Append-only: the existing
 * free-text address input (and its Nominatim/paid geocode chain) is
 * untouched, this is just a faster path when the user has a postcode handy.
 */
export function PostcodeAutocomplete({
  token,
  label,
  onResolved,
}: {
  token?: string | null;
  label: string;
  onResolved: (address: string, postcode: string) => void;
}) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState<PostcodeSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      postcodeApi
        .autocomplete(q.trim(), token)
        .then((res) => {
          setSuggestions(res);
          setOpen(res.length > 0);
        })
        .catch(() => setSuggestions([]));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, token]);

  const select = async (s: PostcodeSuggestion) => {
    setOpen(false);
    setResolving(true);
    try {
      const result = await postcodeApi.lookup(s.code, token);
      onResolved(result.resolved_address ?? s.label, result.postcode);
      setQ("");
      setSuggestions([]);
    } catch (err) {
      // best-effort — fall back to the suggestion label alone
      onResolved(s.label, s.code);
      if (!(err instanceof ApiError)) throw err;
    } finally {
      setResolving(false);
    }
  };

  // Full postcode typed directly (e.g. pasted from a label) — resolve
  // immediately without waiting on autocomplete suggestions.
  useEffect(() => {
    const trimmed = q.trim();
    if (!/^[A-Za-z]{2}-?\d{2}-?[A-Za-z]\d{2}-?[A-Za-z]{2}-?\d{2}$/.test(trimmed)) return;
    setOpen(false);
    setResolving(true);
    postcodeApi
      .lookup(trimmed, token)
      .then((result) => {
        onResolved(result.resolved_address ?? trimmed, result.postcode);
        setQ("");
        setSuggestions([]);
      })
      .catch(() => {
        // not a resolvable code — let the user keep typing/searching
      })
      .finally(() => setResolving(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className="relative space-y-1.5">
      <Label className="flex items-center gap-1 text-xs text-muted-foreground">
        <MapPinned className="h-3 w-3" /> {label}
      </Label>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="e.g. EK 01 A"
        disabled={resolving}
      />
      {open ? (
        <div className="absolute z-10 mt-1 w-full rounded-md border bg-popover shadow-md">
          {suggestions.map((s) => (
            <button
              key={s.code}
              type="button"
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(s)}
            >
              <span className="font-mono text-xs text-muted-foreground">{s.code}</span> {s.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
